import { useConfig } from "@/react/story/providers/ConfigContext";
import { useItems } from "@/react/story/providers/ItemsContext";
import { useStory } from "@/react/story/providers/StoryContext";
import { useCallback, useEffect, useRef } from "react";

type StatsEvent = {
    event: string;
    pageId: number;
    duration?: number;
    href?: string;
}

type StatsPayload = {
    storyId: number;
    companyId: number;
    token: string;
    events: StatsEvent[];
}

type PageStatsOptions = {
    statsEndpoint: string;
    statsToken: string;
}

export default function usePageStats(options: PageStatsOptions) {
    const Config = useConfig();
    const Items = useItems();
    const Story = useStory();

    const bufferRef = useRef<StatsEvent[]>([]);
    const pageEnterRef = useRef<number | null>(null);
    const previousPageIdRef = useRef<number | null>(null);
    const currentRef = useRef(Items.current);

    currentRef.current = Items.current;

    const storyId = Story.story?.id;
    const companyIri = Story.story?.company;

    // L'identifiant est extrait par filtrage, pas par `parseInt` sur l'IRI entier : sur un IRI
    // qui ne finit pas par un nombre, `parseInt` rend `NaN`, que le garde-fou ci-dessous
    // laisserait passer et que `JSON.stringify` sérialiserait en `null`.
    const companyMatch = companyIri?.match(/\/(\d+)$/) ?? null;
    const companyId = companyMatch ? parseInt(companyMatch[1], 10) : null;

    const flush = useCallback(() => {
        // Sans identifiant d'entreprise exploitable ni jeton HMAC, le contrôleur d'ingestion
        // répond 404 : autant garder le tampon que payer l'aller-retour.
        if (
            0 === bufferRef.current.length
            || null === storyId
            || null === companyId
            || "" === options.statsToken
        ) {
            return;
        }

        const events = bufferRef.current.splice(0, bufferRef.current.length);
        const payload: StatsPayload = {
            storyId,
            companyId,
            token: options.statsToken,
            events,
        };

        const blob = new Blob([ JSON.stringify(payload) ], { type: "application/json" });
        navigator.sendBeacon(options.statsEndpoint, blob);
    }, [ storyId, companyId, options.statsToken, options.statsEndpoint ]);

    const recordTimeSpent = useCallback(() => {
        if (null === pageEnterRef.current || null === previousPageIdRef.current) {
            return;
        }

        const duration = Date.now() - pageEnterRef.current;
        if (duration >= 500) {
            bufferRef.current.push({
                event: "page_time",
                pageId: previousPageIdRef.current,
                duration,
            });
        }

        pageEnterRef.current = null;
    }, []);

    // Track page views and time
    useEffect(() => {
        if (!Config.isOnline) {
            return;
        }

        const currentPageId = Items.current?.page?.id ?? null;
        if (null === currentPageId) {
            return;
        }

        // Record time on previous page
        recordTimeSpent();

        // Track page view
        bufferRef.current.push({
            event: "page_view",
            pageId: currentPageId,
        });

        // Start timing for new page
        pageEnterRef.current = Date.now();
        previousPageIdRef.current = currentPageId;
    }, [ Config.isOnline, Items.current?.id ]);

    // Periodic flush (every 10s)
    useEffect(() => {
        if (!Config.isOnline) {
            return;
        }

        const interval = setInterval(() => flush(), 10_000);

        return () => clearInterval(interval);
    }, [ Config.isOnline, flush ]);

    // Flush on visibility change and page hide
    useEffect(() => {
        if (!Config.isOnline) {
            return;
        }

        const handleVisibilityChange = () => {
            if ("hidden" === document.visibilityState) {
                recordTimeSpent();
                flush();
            } else if ("visible" === document.visibilityState) {
                const currentPageId = currentRef.current?.page?.id ?? null;
                if (null !== currentPageId) {
                    pageEnterRef.current = Date.now();
                    previousPageIdRef.current = currentPageId;
                }
            }
        };

        const handlePageHide = () => {
            recordTimeSpent();
            flush();
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("pagehide", handlePageHide);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("pagehide", handlePageHide);
        };
    }, [ Config.isOnline, flush, recordTimeSpent ]);

    // Vidage au démontage. `flush` et `recordTimeSpent` sont tenus dans une `ref` mise à jour
    // par un effet : l'identité de `flush` change avec la story et les options, donc un effet
    // qui en dépendrait verrait son nettoyage se déclencher hors démontage et enverrait en
    // double. Le tableau de dépendances vide, lui, ne se nettoie qu'au démontage réel.
    const flushOnUnmountRef = useRef<() => void>(() => undefined);

    useEffect(() => {
        flushOnUnmountRef.current = () => {
            recordTimeSpent();
            flush();
        };
    }, [ flush, recordTimeSpent ]);

    useEffect(() => () => flushOnUnmountRef.current(), []);

    const trackLinkClick = useCallback((pageId: number, href: string) => {
        if (!Config.isOnline) {
            return;
        }

        bufferRef.current.push({
            event: "link_click",
            pageId,
            href,
        });
    }, [ Config.isOnline ]);

    return { trackLinkClick };
}
